import { Input } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { AttachmentPills } from 'features/chatbot/components/AttachmentPills'
import { DocumentAttachmentPopover } from 'features/chatbot/components/DocumentAttachmentPopover'
import { useChatbotAttachments } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { ChatbotDataContext } from 'features/chatbot/contexts/ChatbotDataLayer'
import { ChatbotUXContext } from 'features/chatbot/contexts/ChatbotUXLayer'
import { ArrowUp } from 'lucide-react'
import React, { useContext, useRef, useState } from 'react'
import styled from 'styled-components'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  width: 100%;
`

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledTextArea = styled(Input.TextArea)<{ $hasAttachments: boolean }>`
  width: 100%;
  border-radius: 0.5rem;
  padding: ${({ $hasAttachments }) =>
    $hasAttachments
      ? '3.5rem 2.5rem 0.75rem 2.5rem'
      : '0.75rem 2.5rem 0.75rem 2.5rem'};
  background-color: ${({ theme }) => theme.forecast.cardBackground} !important;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.defaultText};

  &::placeholder {
    color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:focus,
  &:focus-within {
    border-color: ${({ theme }) => theme.forecast.primary} !important;
    box-shadow: none !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.forecast.border2};
  }
`

const SubmitButton = styled(CantileverButton)`
  position: absolute;
  bottom: 0.75rem;
  right: 0.5rem;
  min-height: 1.5rem;
  width: 1.5rem;
  z-index: 1;
`

const AttachmentPillsWrapper = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 2.5rem;
  right: 2.5rem;
  z-index: 1;
  pointer-events: none;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    pointer-events: auto;
  }
`

interface ChatbotInputCardProps {
  delphiHelpModalOpen: boolean
  setDelphiHelpModalOpen: (open: boolean) => void
}

export const ChatbotInputCard: React.FC<ChatbotInputCardProps> = () => {
  const { formRef, handleEnterKeyDown, handleSendDelphiMessage } =
    useContext(ChatbotUXContext)
  const { isDelphiThinking } = useContext(ChatbotDataContext)
  const [inputValue, setInputValue] = useState('')
  const attachmentContext = useChatbotAttachments()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (inputValue.trim() && !isDelphiThinking) {
      handleSendDelphiMessage(inputValue)
      setInputValue('')
    }
  }

  const hasAttachments = attachmentContext?.attachments.length > 0

  return (
    <FormContainer
      ref={formRef}
      onSubmit={handleSubmit}
      onKeyDown={handleEnterKeyDown}>
      <TextAreaWrapper>
        <DocumentAttachmentPopover
          onDocumentSelected={() => textAreaRef.current?.focus()}
        />
        {hasAttachments && (
          <AttachmentPillsWrapper>
            <AttachmentPills />
          </AttachmentPillsWrapper>
        )}
        <StyledTextArea
          ref={textAreaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder='Ask a question...'
          autoSize={{ minRows: 1, maxRows: 6 }}
          $hasAttachments={hasAttachments}
        />
        <SubmitButton
          iconOnly
          aria-label='Submit'
          primary
          onClick={handleSubmit}
          disabled={isDelphiThinking}>
          <ArrowUp size={16} />
        </SubmitButton>
      </TextAreaWrapper>
    </FormContainer>
  )
}
